/**
 * Expression Parser for Amount Keyboard
 * Supports basic arithmetic operations: addition (+) and subtraction (-)
 * Evaluates expressions left-to-right (no operator precedence)
 */

/**
 * Parse and evaluate a mathematical expression
 * @param expr - Expression string (e.g., "50000 + 25000 - 10000")
 * @returns Calculated result or null if invalid
 */
export const parseExpression = (expr: string): number | null => {
  if (!expr.trim()) return 0;

  // Remove all spaces
  const clean = expr.replace(/\s+/g, '');

  // Check for invalid patterns
  if (/[+\-]{2,}/.test(clean)) return null; // consecutive operators
  if (/[+\-]$/.test(clean)) return null; // ends with operator
  if (/^[+\-]/.test(clean)) return null; // starts with operator

  try {
    // Split by operators while keeping them
    const tokens = clean.split(/([+\-])/);

    // First token should be a number
    let result = parseFloat(tokens[0]);
    if (isNaN(result)) return null;

    // Process remaining tokens in pairs (operator, operand)
    for (let i = 1; i < tokens.length; i += 2) {
      const operator = tokens[i];
      const operand = parseFloat(tokens[i + 1]);

      if (isNaN(operand)) return null;

      if (operator === '+') {
        result += operand;
      } else if (operator === '-') {
        result -= operand;
      }
    }

    return Math.round(result);
  } catch {
    return null;
  }
};

/**
 * Format expression with proper spacing around operators
 * @param expr - Raw expression string
 * @returns Formatted expression with spaces
 */
export const formatExpression = (expr: string): string => {
  if (!expr) return '';

  // Add spaces around operators
  return expr
    .replace(/\s+/g, '') // Remove existing spaces
    .replace(/([+\-])/g, ' $1 ') // Add spaces around operators
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .trim();
};

/**
 * Check if expression is valid and complete
 * @param expr - Expression string to validate
 * @returns true if valid, false otherwise
 */
export const isValidExpression = (expr: string): boolean => {
  if (!expr.trim()) return true; // Empty is valid (treated as 0)

  const clean = expr.replace(/\s+/g, '');

  // Check for invalid patterns
  if (/[+\-]{2,}/.test(clean)) return false; // consecutive operators
  if (/[+\-]$/.test(clean)) return false; // ends with operator
  if (/^[+\-]/.test(clean)) return false; // starts with operator

  // Try to parse - if it returns a number, it's valid
  const result = parseExpression(expr);
  return result !== null;
};

/**
 * Check if adding a character would exceed the digit limit
 * @param expr - Current expression
 * @param maxDigits - Maximum number of digits allowed
 * @returns true if within limit, false otherwise
 */
export const isWithinDigitLimit = (expr: string, maxDigits: number = 12): boolean => {
  // Count only digits in the expression
  const digitCount = (expr.match(/\d/g) || []).length;
  return digitCount < maxDigits;
};

/**
 * Get the last token in the expression (number or operator)
 * @param expr - Expression string
 * @returns Last token or empty string
 */
export const getLastToken = (expr: string): string => {
  if (!expr) return '';
  const clean = expr.replace(/\s+/g, '');
  const tokens = clean.split(/([+\-])/);
  return tokens[tokens.length - 1] || '';
};

/**
 * Check if the last character is an operator
 * @param expr - Expression string
 * @returns true if last char is an operator
 */
export const endsWithOperator = (expr: string): boolean => {
  const clean = expr.replace(/\s+/g, '');
  return /[+\-]$/.test(clean);
};
